import React, { useState } from "react";
import { Alert, Nav, Tab } from "react-bootstrap";
import { useSearchParams } from "react-router";
import { usePost } from "../../api";
import { PageHeader, PageToolbar, ToolbarButton } from "../../components/ui";
import { OpenDataReadinessList } from "./readiness-list";
import { OpenDataReadinessRuns } from "./readiness-runs";
import { OpenDataReadinessTrend } from "./readiness-trend";

type TabKey = "merchants" | "trend" | "runs";

const TABS: { key: TabKey; label: string; hint: string }[] = [
  {
    key: "merchants",
    label: "Merchants",
    hint: "Every swept merchant, graded on the latest snapshot",
  },
  {
    key: "trend",
    label: "Trend",
    hint: "How many merchants were ready, day by day",
  },
  { key: "runs", label: "Runs", hint: "Sweep history and anything in flight" },
];

export const OpenDataReadiness: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const active = (params.get("view") as TabKey) ?? "merchants";
  const [notice, setNotice] = useState<string | null>(null);
  const [queueing, setQueueing] = useState(false);

  const { execute: post } = usePost<{ itemsConsidered: number }, unknown>();

  const runSweep = async () => {
    setQueueing(true);
    setNotice(null);
    try {
      const result = await post("open-data-readiness/runs", {});
      setNotice(
        `Sweep queued for ${result?.itemsConsidered ?? 0} merchants. The runner paces it against the Open Data call budget, so a large list takes a few minutes.`
      );
    } catch (err: any) {
      setNotice(err?.data?.message ?? "Could not queue the sweep.");
    } finally {
      setQueueing(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Open Data Readiness"
        description="A paced sweep of the Open Data checks across every QA list opted in, graded red / amber / green and snapshotted daily."
      />

      <PageToolbar>
        <ToolbarButton
          label="Run sweep"
          hint="Re-check every QA list toggled into Open Data against the live APIs. One sweep runs at a time."
          icon="bi-play-fill"
          variant="primary"
          onClick={runSweep}
          disabled={queueing}
        />
        <ToolbarButton
          label="QA lists"
          hint="Toggle which lists the sweep covers"
          icon="bi-list-check"
          to="/qa-lists"
        />
      </PageToolbar>

      {notice && (
        <Alert variant="info" dismissible onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      <Tab.Container
        activeKey={active}
        onSelect={(k) => k && setParams({ view: k }, { replace: true })}
      >
        <Nav variant="tabs" className="mb-3">
          {TABS.map((t) => (
            <Nav.Item key={t.key}>
              <Nav.Link eventKey={t.key} title={t.hint}>
                {t.label}
              </Nav.Link>
            </Nav.Item>
          ))}
        </Nav>
        <Tab.Content>
          <Tab.Pane eventKey="merchants" mountOnEnter>
            <OpenDataReadinessList />
          </Tab.Pane>
          <Tab.Pane eventKey="trend" mountOnEnter>
            <OpenDataReadinessTrend />
          </Tab.Pane>
          <Tab.Pane eventKey="runs" mountOnEnter>
            <OpenDataReadinessRuns />
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </div>
  );
};
